import React, { useState, useEffect, useRef, ReactNode } from 'react';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import { Image } from '@arctheme-components/elements/Image/Image';
import clsx from 'clsx';
import './Carousel.scss';

type CarouselProps = {
  settings: {
    mobile_breakpoint?: number;
    section?: {
      color_schema?: string;
      autoplay?: boolean;
      autoplay_speed?: number;
      show_navigation?: boolean;
      show_pagination?: boolean;
      loop?: boolean;
      slide_gap?: number;
      slides_per_view?: number;
      slides_per_view_mobile?: number;
      aspect_ratio?: string;
      custom_height?: number;
      fade_partial?: boolean;
    };
  };
  children?: ReactNode;
  blocks?: any[];
  slides?: {
    id: string;
    content: ReactNode;
    alt?: string;
    url?: string;
  }[];
};

export const Carousel = ({ settings, children, blocks, slides = [] }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);

  // State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoplayInterval, setAutoplayInterval] = useState<NodeJS.Timeout | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [initialPosition, setInitialPosition] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Props with defaults
  const { section } = settings;
  const showNavigation = section?.show_navigation !== false;
  const showPagination = section?.show_pagination !== false;
  const autoplay = section?.autoplay || false;
  const autoplaySpeed = section?.autoplay_speed || 3000;
  const loop = section?.loop !== false;
  const slideGap = section?.slide_gap || 16;
  const slidesPerView = isMobile
    ? section?.slides_per_view_mobile || 1
    : section?.slides_per_view || 1;
  const fadePartialSlides = section?.fade_partial || false;
  
  // Get carousel content from blocks or props
  const carouselSlides = slides.length > 0 
    ? slides 
    : blocks?.map((block, index) => ({
        id: block.id || `slide-${index}`,
        content: block.content || block,
        alt: block.settings?.alt || '',
        url: block.settings?.image_url || block.settings?.url || ''
      })) || [];

  // Initialize
  useEffect(() => {
    if (!isInitialized && carouselRef.current) {
      setTotalSlides(carouselSlides.length);
      setIsInitialized(true);
      
      // Set up autoplay if enabled
      if (autoplay) {
        const interval = setInterval(() => {
          nextSlide();
        }, autoplaySpeed);
        setAutoplayInterval(interval);
      }
      
      return () => {
        if (autoplayInterval) {
          clearInterval(autoplayInterval);
        }
      };
    }
  }, [isInitialized, carouselSlides.length, autoplay]);

  // Handle slide changes
  const goToSlide = (index: number) => {
    if (isAnimating) return;

    let nextIndex = index;
    
    // Handle looping
    if (loop) {
      if (nextIndex < 0) {
        nextIndex = totalSlides - 1;
      } else if (nextIndex >= totalSlides) {
        nextIndex = 0;
      }
    } else {
      // Constrain to valid slide indices when not looping
      nextIndex = Math.max(0, Math.min(nextIndex, totalSlides - 1));
    }
    
    if (nextIndex === currentSlide) return;

    setIsAnimating(true);
    setCurrentSlide(nextIndex);

    // Reset animation state after transition completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const nextSlide = () => {
    goToSlide(currentSlide + 1);
  };

  const prevSlide = () => {
    goToSlide(currentSlide - 1);
  };

  // Handle touch events for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      setAutoplayInterval(null);
    }
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    // Detect swipe direction
    if (touchStart - touchEnd > 50) {
      // Swipe left
      nextSlide();
    } else if (touchStart - touchEnd < -50) {
      // Swipe right
      prevSlide();
    }
    
    // Restart autoplay if enabled
    if (autoplay && !autoplayInterval) {
      const interval = setInterval(() => {
        nextSlide();
      }, autoplaySpeed);
      setAutoplayInterval(interval);
    }
  };

  // Handle mouse drag events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      setAutoplayInterval(null);
    }
    setIsDragging(true);
    setInitialPosition(e.clientX);
    setCurrentPosition(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setCurrentPosition(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      const dragDistance = initialPosition - currentPosition;
      
      // Detect drag direction
      if (dragDistance > 50) {
        // Dragged left
        nextSlide();
      } else if (dragDistance < -50) {
        // Dragged right
        prevSlide();
      }
      
      setIsDragging(false);
      
      // Restart autoplay if enabled
      if (autoplay && !autoplayInterval) {
        const interval = setInterval(() => {
          nextSlide();
        }, autoplaySpeed);
        setAutoplayInterval(interval);
      }
    }
  };
  
  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      
      // Restart autoplay if enabled
      if (autoplay && !autoplayInterval) {
        const interval = setInterval(() => {
          nextSlide();
        }, autoplaySpeed);
        setAutoplayInterval(interval);
      }
    }
  };

  // Generate dynamic slide styles
  const getSlideStyles = (index: number) => {
    const isActive = index === currentSlide;
    const isNext = loop 
      ? (index === (currentSlide + 1) % totalSlides) 
      : (index === currentSlide + 1);
    const isPrev = loop 
      ? (index === (currentSlide - 1 + totalSlides) % totalSlides) 
      : (index === currentSlide - 1);
      
    const translateValue = `${-100 * currentSlide}%`;
    
    return {
      transform: `translateX(${translateValue})`,
      marginRight: `${slideGap}px`,
      opacity: fadePartialSlides && !isActive && !isNext && !isPrev ? 0.5 : 1,
      transition: isAnimating ? 'transform 0.3s ease, opacity 0.3s ease' : 'none'
    };
  };

  return (
    <div 
      className={clsx(
        'carousel-component', 
        `color-${section?.color_schema || 'light'}`,
        { 'is-animating': isAnimating }
      )}
      ref={carouselRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className="carousel-track"
        ref={slidesContainerRef}
      >
        <div 
          className="carousel-slides-container" 
          style={{ 
            gridTemplateColumns: `repeat(${totalSlides}, 100%)`,
            gap: slideGap
          }}
        >
          {carouselSlides.map((slide, index) => (
            <div 
              key={slide.id || index}
              className={clsx(
                'carousel-slide', 
                { 
                  'active': index === currentSlide,
                  'next': loop 
                    ? index === (currentSlide + 1) % totalSlides
                    : index === currentSlide + 1,
                  'prev': loop 
                    ? index === (currentSlide - 1 + totalSlides) % totalSlides
                    : index === currentSlide - 1
                }
              )}
              style={getSlideStyles(index)}
            >
              {slide.url ? (
                <Image 
                  imageUrl={slide.url}
                  alt={slide.alt || `Slide ${index + 1}`}
                  classes="carousel-slide-image w-full h-full object-cover"
                />
              ) : (
                <div className="carousel-slide-content">
                  {typeof slide.content === 'object' ? slide.content : children}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showNavigation && totalSlides > 1 && (
        <div className="carousel-navigation">
          <button 
            className="carousel-prev" 
            onClick={(e) => { 
              e.preventDefault(); 
              prevSlide(); 
            }}
            aria-label="Previous slide"
            disabled={!loop && currentSlide === 0}
          >
            <span className="sr-only">Previous</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className="carousel-next" 
            onClick={(e) => { 
              e.preventDefault(); 
              nextSlide(); 
            }}
            aria-label="Next slide"
            disabled={!loop && currentSlide === totalSlides - 1}
          >
            <span className="sr-only">Next</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {showPagination && totalSlides > 1 && (
        <div className="carousel-pagination" ref={paginationRef}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={clsx('carousel-dot', { 'active': index === currentSlide })}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;